import { useCallback, useEffect, useMemo } from 'react';
import { useRecoilState, useRecoilValue } from 'recoil';
import {
    Color,
    DICOMApprovalStatus,
    Roi,
    RoiIdentificationCodeSequence,
    RoiInterpretedType, // Assuming this is the type for interpretedType
} from '~/domain/dicom/dicomstructureset';
import { environmentState } from '~/state/atoms/engineAtoms';
import {
    globalROIContourThicknessState,
    ROIState,
    roiState,
} from '~/state/atoms/structureSet/roiAtoms';
import { handleError } from '~/utils/errors';
import useStructureSet from './useStructureSet';

const useRoi = (roiInstance: Roi) => {
    const { structureSet } = useStructureSet();
    const [roi, setRoi] = useRecoilState(
        roiState({
            roiNumber: roiInstance.roiNumber,
            sopUid: structureSet?.sopUid ?? 'default',
        })
    );
    const env = useRecoilValue(environmentState);
    const getRoiVolumeCm3 = () => {
        return env?.getRoiVolumeCm3(roiInstance.roiNumber) ?? Number.NaN;
    };

    const getRoiEquivalentSphereDiameter = () => {
        return 2 * Math.cbrt((3 * getRoiVolumeCm3()) / (4 * Math.PI));
    };

    const toString = (code: RoiIdentificationCodeSequence | undefined) => {
        return code
            ? `${code.codeValue} ${code.codingSchemeDesignator} ${code.codeMeaning}`
            : '';
    };

    const defaultState: ROIState = {
        name: roiInstance.name,
        color: roiInstance.color,
        visible: roiInstance.visible,
        approvalStatus: roiInstance.approvalStatus,
        contourThickness: roiInstance.contourThickness,
        interpretedType: roiInstance.interpretedType,
        relativeElectronDensity: roiInstance.relativeElectronDensity,
        densityOverride: roiInstance.densityOverride,
        identificationCodeDisplay: roiInstance.identificationCode ? `${roiInstance.identificationCode?.codeValue} | ${roiInstance.identificationCode?.codingSchemeDesignator} | ${roiInstance.identificationCode?.codingSchemeVersion}` : 'Not defined',
        roiDescription: roiInstance.roiDescription,
        roiNumber: roiInstance.roiNumber,
        roiGenerationAlgorithm: roiInstance.roiGenerationAlgorithm,
    };

    useEffect(() => {
        if (!roi) setRoi(defaultState);
    }, []);

    useEffect(() => {
        setRoiApprovalStatus(roiInstance.approvalStatus);
    }, [roiInstance.approvalStatus]);

    useEffect(() => {
        setVisible(roiInstance.visible);
    }, [roiInstance.visible]);

    const setVisible = useCallback(
        (visible: boolean) => {
            try {
                roiInstance.visible = visible;
                setRoi((prev) => ({
                    ...(prev ?? defaultState),
                    visible: roiInstance.visible,
                }));
            } catch (error) {
                handleError(`Error updating visibility: ${error}`);
            }
        },
        [roiInstance, setRoi]
    );

    const setName = useCallback(
        (newName: string) => {
            try {
                roiInstance.name = newName;
                setRoi((prev) => ({
                    ...(prev ?? defaultState),
                    name: roiInstance.name,
                }));
            } catch (error) {
                handleError(`Error updating name: ${error}`);
            }
        },
        [roiInstance, setRoi]
    );

    const setColor = useCallback(
        (newColor: Color) => {
            try {
                roiInstance.color = newColor;
                setRoi((prev) => ({
                    ...(prev ?? defaultState),
                    color: roiInstance.color,
                }));
            } catch (error) {
                handleError(`Error updating color: ${error}`);
            }
        },
        [roiInstance, setRoi]
    );

    const setContourThickness = useCallback(
        (newThickness: number) => {
            try {
                roiInstance.contourThickness = newThickness;
                setRoi((prev) => ({
                    ...(prev ?? defaultState),
                    contourThickness: roiInstance.contourThickness,
                }));
            } catch (error) {
                handleError(`Error updating contour thickness: ${error}`);
            }
        },
        [roiInstance, setRoi]
    );

    const globalROIContourThickness = useRecoilValue(
        globalROIContourThicknessState
    );

    useEffect(() => {
        setContourThickness(globalROIContourThickness);
    }, [globalROIContourThickness, setContourThickness]);

    const setRoiApprovalStatus = useCallback(
        (newStatus: DICOMApprovalStatus) => {
            try {
                roiInstance.approvalStatus = newStatus;
                setRoi((prev) => ({
                    ...(prev ?? defaultState),
                    approvalStatus: roiInstance.approvalStatus,
                }));
            } catch (error) {
                handleError(`Error updating approval status: ${error}`);
            }
        },
        [roiInstance, setRoi]
    );

    const setInterpretedType = useCallback(
        (newType: RoiInterpretedType) => {
            try {
                roiInstance.interpretedType = newType;
                setRoi((prev) => ({
                    ...(prev ?? defaultState),
                    interpretedType: roiInstance.interpretedType,
                }));
            } catch (error) {
                handleError(`Error updating interpreted type: ${error}`);
            }
        },
        [roiInstance, setRoi]
    );

    const setElectronDensityOverride = useCallback(
        (newDensity: number) => {
            try {
                roiInstance.densityOverride = newDensity;
                setRoi((prev) => ({
                    ...(prev ?? defaultState),
                    densityOverride:
                        roiInstance.densityOverride ?? Number.NaN,
                }));
            } catch (error) {
                handleError(
                    `Error updating electron density override: ${error}`
                );
            }
        },
        [roiInstance, setRoi]
    );

    const setRoiDescription = useCallback(
        (newDescription: string) => {
            try {
                roiInstance.roiDescription = newDescription;
                setRoi((prev) => ({
                    ...(prev ?? defaultState),
                    roiDescription: roiInstance.roiDescription,
                }));
            } catch (error) {
                handleError(`Error updating ROI description: ${error}`);
            }
        },
        [roiInstance, setRoi]
    );

    const structureCanBeEdited = useMemo(
        () => roi?.approvalStatus === DICOMApprovalStatus.UNAPPROVED,
        [roi]
    );
    const structureCanBeDeleted = useMemo(
        () => roi?.approvalStatus !== DICOMApprovalStatus.APPROVED,
        [roi]
    );

    return {
        roi,
        setName,
        setColor,
        setVisible,
        setContourThickness,
        setRoiApprovalStatus,
        setInterpretedType,
        setElectronDensityOverride,
        setRoiDescription,
        structureCanBeEdited,
        structureCanBeDeleted,
        getRoiVolumeCm3,
        getRoiEquivalentSphereDiameter,
    };
};

export default useRoi;
